import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase/config';
import { collection, getDocs } from 'firebase/firestore';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const monthsShort = (d: Date) => d.toLocaleString('pt-BR', { month: 'short' });

const AdminRevenueChart: React.FC<{ months?: number, granularity?: 'monthly'|'weekly'|'daily' }> = ({ months = 6, granularity = 'monthly' }) => {
  const [payments, setPayments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const snap = await getDocs(collection(db, 'payments'));
      const list = snap.docs.map(d => ({ id: d.id, ...(d.data() as any) }));
      setPayments(list);
      setLoading(false);
    };
    fetch();
  }, []);

  const data = useMemo(() => {
    const now = new Date();
    if (granularity === 'monthly') {
      const buckets: { label: string; value: number }[] = [];
      for (let i = months - 1; i >= 0; i--) {
        const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
        buckets.push({ label: monthsShort(date), value: 0 });
      }
      payments.forEach(p => {
        const created = p.createdAt?.toDate ? p.createdAt.toDate() : (p.createdAt ? new Date(p.createdAt) : null);
        if (!created) return;
        const diffMonths = (now.getFullYear() - created.getFullYear()) * 12 + (now.getMonth() - created.getMonth());
        if (diffMonths < months && diffMonths >= 0) {
          const idx = months - 1 - diffMonths;
          buckets[idx].value += Number(p.amount || 0);
        }
      });
      return buckets.map(b => ({ ...b, value: Number(b.value.toFixed(2)) }));
    }

    if (granularity === 'weekly') {
      // last `months` weeks (months param used as count here)
      const weeks = months;
      const buckets: { label: string; value: number }[] = [];
      for (let i = weeks - 1; i >= 0; i--) {
        const start = new Date(now);
        start.setDate(start.getDate() - i*7);
        buckets.push({ label: `W-${i+1}`, value: 0 });
      }
      payments.forEach(p => {
        const created = p.createdAt?.toDate ? p.createdAt.toDate() : (p.createdAt ? new Date(p.createdAt) : null);
        if (!created) return;
        const diffDays = Math.floor((now.getTime() - created.getTime())/(1000*60*60*24));
        const diffWeeks = Math.floor(diffDays / 7);
        if (diffWeeks < weeks && diffWeeks >= 0) {
          const idx = weeks - 1 - diffWeeks;
          buckets[idx].value += Number(p.amount || 0);
        }
      });
      return buckets.map(b => ({ ...b, value: Number(b.value.toFixed(2)) }));
    }

    // daily
    const days = months; // months param used as number of days
    const bucketsDaily: { label: string; value: number }[] = [];
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      bucketsDaily.push({ label: d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }), value: 0 });
    }
    payments.forEach(p => {
      const created = p.createdAt?.toDate ? p.createdAt.toDate() : (p.createdAt ? new Date(p.createdAt) : null);
      if (!created) return;
      const diffDays = Math.floor((new Date().getTime() - created.getTime())/(1000*60*60*24));
      if (diffDays < days && diffDays >= 0) {
        const idx = days - 1 - diffDays;
        bucketsDaily[idx].value += Number(p.amount || 0);
      }
    });
    return bucketsDaily.map(b => ({ ...b, value: Number(b.value.toFixed(2)) }));
  }, [payments, months, granularity]);

  if (loading) return <div className="section-block"><p>Carregando gráfico...</p></div>;

  return (
    <div className="section-block">
      <h3>Receita ({granularity === 'monthly' ? `últimos ${months} meses` : granularity === 'weekly' ? `últimas ${months} semanas` : `últimos ${months} dias`})</h3>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <LineChart data={data}>
            <XAxis dataKey="label" />
            <YAxis tickFormatter={(v:any) => `R$ ${Number(v || 0).toLocaleString('pt-br', { minimumFractionDigits: 0 })}`} />
            <Tooltip formatter={(v:any) => `R$ ${Number(v || 0).toLocaleString('pt-br', { minimumFractionDigits: 2 })}`} />
            <Line type="monotone" dataKey="value" stroke="#06b6d4" strokeWidth={3} dot={false} isAnimationActive={true} animationBegin={200} animationDuration={800} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AdminRevenueChart;